import { memo, useCallback, useMemo } from 'react';
import { Box, Icon, Text } from '@bonanza-social/moonlight';
import { getMilestoneFlipReward } from '../../../../../utils/jackpot';
import { FlipRewardProps } from './FlipReward.types';
import {
  StackNavigationProp,
  useFocusEffect,
  useNavigation,
} from '@bonanza-social/navigation';
import { CommunityJackpotNavigatorParamsList } from '../../../../../navigators/CommunityJackpotNavigator.types';
import { Pressable } from 'react-native';
import {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { formatNumber } from '@bonanza-social/toolkit/utils';

const CONTAINER_HEIGHT = 47;

function FlipReward({ activeMilestone, animatable, ...rest }: FlipRewardProps) {
  const navigation =
    useNavigation<StackNavigationProp<CommunityJackpotNavigatorParamsList>>();

  const translateY = useSharedValue(0);
  const style = useAnimatedStyle(() => ({
    transform: [
      {
        translateY: translateY.value,
      },
    ],
  }));

  useFocusEffect(() => {
    if (animatable) {
      translateY.value = -CONTAINER_HEIGHT;
      translateY.value = withTiming(-(CONTAINER_HEIGHT + 25), {
        duration: 500,
        easing: Easing.in(Easing.ease),
      });

      return;
    }

    translateY.value = -(CONTAINER_HEIGHT + 10);
  });

  const reward = useMemo(
    () => activeMilestone && getMilestoneFlipReward(activeMilestone),
    [activeMilestone],
  );

  const onAllPrizesPress = useCallback(() => {
    if (activeMilestone) {
      navigation.navigate('CommunityJackpotRewardsScreen', {
        milestoneId: activeMilestone.id,
      });
    }
  }, [activeMilestone, navigation]);

  if (!reward) {
    return null;
  }

  return (
    <Box
      position="absolute"
      top="space-0"
      gap="space-200"
      borderRadius="radius-200"
      backgroundColor="secondaryBackground"
      animatedStyle={style}
      zIndex={2}
      height={CONTAINER_HEIGHT}
      minWidth={90}
      centerContent
      {...rest}
    >
      <Box
        position="absolute"
        bottom="space-0"
        width="space-800"
        height="space-800"
        backgroundColor="secondaryBackground"
        transform="rotate(45deg)"
      />
      <Pressable onPress={onAllPrizesPress}>
        <Text
          variant="badge"
          locKey="flipRewardTitle"
          locNS="communityJackpot"
          color="darkSurfacePrimary"
          textAlign="center"
          uppercase
        />
        <Box
          flexDirection="row"
          gap="space-100"
          marginTop="space-100"
          justifyContent="center"
        >
          <Icon
            name="bonanzaCoin"
            width="space-200"
            height="space-200"
            color="iconSecondary"
          />
          <Text variant="badge" color="darkSurfacePrimary">
            {formatNumber(reward.rewardAmount)}
          </Text>
        </Box>
      </Pressable>
    </Box>
  );
}

export default memo(FlipReward);
